import type { LegalDocumentType } from "@/components/features/notarial-requests/legal-entity-request-fields";
import type { IncomeCertificateSubmitPayload } from "@/components/features/notarial-requests/income-certificate-wizard";
import type { ValidateRutResponse } from "@/services/authService";
import type { IncomeCertificateDetailsBody, UserNotarialRequestDocumentType } from "@/services/userNotarialRequestsService";

const DB_NAME = "gestionalo_portal_v1";
const STORE = "certificate_payment_resume";
const DB_VERSION = 1;

export type SerializedFileBlob = {
  name: string;
  type: string;
  buffer: ArrayBuffer;
};

export type CertificatePaymentResumeRecordV1 =
  | {
      checkoutId: string;
      version: 1;
      kind: "simple_notary";
      requestType: string;
    }
  | {
      checkoutId: string;
      version: 1;
      kind: "simple_accountant";
      requestType: string;
    }
  | {
      checkoutId: string;
      version: 1;
      kind: "legal_entity";
      fulfillmentMode: string;
      rut: string;
      validatedCompany: ValidateRutResponse;
      /** Absent on legacy IndexedDB resumes; treat as false. */
      companyRutUnverified?: boolean;
      legalDocumentType: LegalDocumentType;
      files: {
        estatuto: SerializedFileBlob;
        comunicacion_bcu: SerializedFileBlob;
        legal_document_front?: SerializedFileBlob;
        legal_document_back?: SerializedFileBlob;
        passport_document?: SerializedFileBlob;
      };
      invoiceIncludeRut?: boolean;
      invoiceRutDigits?: string | null;
    }
  | {
      checkoutId: string;
      version: 1;
      kind: "income_certificate";
      fulfillmentMode: string;
      incomeCertificateDetails: IncomeCertificateDetailsBody;
      companyRutUnverified?: boolean;
      companySnapshot?: IncomeCertificateSubmitPayload["companySnapshot"];
      uploads: Array<
        {
          docType: UserNotarialRequestDocumentType;
        } & SerializedFileBlob
      >;
      invoiceIncludeRut?: boolean;
      invoiceRutDigits?: string | null;
    };

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = (): void => {
      reject(req.error ?? new Error("IndexedDB open failed"));
    };
    req.onsuccess = (): void => {
      resolve(req.result);
    };
    req.onupgradeneeded = (): void => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "checkoutId" });
      }
    };
  });
}

export async function putCertificatePaymentResume(record: CertificatePaymentResumeRecordV1): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.oncomplete = (): void => {
      resolve();
    };
    tx.onerror = (): void => {
      reject(tx.error ?? new Error("IndexedDB transaction failed"));
    };
    tx.objectStore(STORE).put(record);
  });
}

/**
 * Read the resume blob WITHOUT deleting it. The blob stays in IndexedDB so
 * a failed createRequest after payment doesn't strand the user — they
 * (or a retry) can hydrate again from the same `?checkout_id=` URL.
 * Pair with [consumeCertificatePaymentResume] once the create + upload
 * sequence has fully landed.
 */
export async function peekCertificatePaymentResume(
  checkoutId: string,
): Promise<CertificatePaymentResumeRecordV1 | null> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const store = tx.objectStore(STORE);
    const getReq = store.get(checkoutId);
    let found: CertificatePaymentResumeRecordV1 | null = null;
    getReq.onerror = (): void => {
      reject(getReq.error ?? new Error("IndexedDB get failed"));
    };
    getReq.onsuccess = (): void => {
      const val = getReq.result as CertificatePaymentResumeRecordV1 | undefined;
      if (val) found = val;
    };
    tx.oncomplete = (): void => resolve(found);
    tx.onerror = (): void => {
      reject(tx.error ?? new Error("IndexedDB transaction failed"));
    };
  });
}

/**
 * Delete the resume blob — call ONLY after the request has been created
 * and the documents uploaded successfully. A retry-safe failure path
 * needs the blob to survive, which is why this is split from the peek.
 */
export async function consumeCertificatePaymentResume(
  checkoutId: string,
): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(checkoutId);
    tx.oncomplete = (): void => resolve();
    tx.onerror = (): void => {
      reject(tx.error ?? new Error("IndexedDB transaction failed"));
    };
  });
}

async function fileToBlob(file: File): Promise<SerializedFileBlob> {
  const buffer = await file.arrayBuffer();
  return { name: file.name, type: file.type || "application/octet-stream", buffer };
}

type LegalEntityResumeFiles = Extract<CertificatePaymentResumeRecordV1, { kind: "legal_entity" }>["files"];

export async function buildLegalEntityPaymentResume(params: {
  checkoutId: string;
  fulfillmentMode: string;
  rut: string;
  validatedCompany: ValidateRutResponse;
  companyRutUnverified: boolean;
  legalDocumentType: LegalDocumentType;
  estatutoFile: File;
  comunicacionBcuFile: File;
  legalDocumentFrontFile: File | null;
  legalDocumentBackFile: File | null;
  passportDocumentFile: File | null;
  invoiceIncludeRut?: boolean;
  invoiceRutDigits?: string | null;
}): Promise<CertificatePaymentResumeRecordV1> {
  const files: LegalEntityResumeFiles = {
    estatuto: await fileToBlob(params.estatutoFile),
    comunicacion_bcu: await fileToBlob(params.comunicacionBcuFile),
  };
  if (params.legalDocumentType === "national_id") {
    if (params.legalDocumentFrontFile && params.legalDocumentBackFile) {
      files.legal_document_front = await fileToBlob(params.legalDocumentFrontFile);
      files.legal_document_back = await fileToBlob(params.legalDocumentBackFile);
    }
  } else if (params.passportDocumentFile) {
    files.passport_document = await fileToBlob(params.passportDocumentFile);
  }
  return {
    checkoutId: params.checkoutId,
    version: 1,
    kind: "legal_entity",
    fulfillmentMode: params.fulfillmentMode,
    rut: params.rut,
    validatedCompany: params.validatedCompany,
    companyRutUnverified: params.companyRutUnverified,
    legalDocumentType: params.legalDocumentType,
    files,
    invoiceIncludeRut: params.invoiceIncludeRut,
    invoiceRutDigits:
      params.invoiceIncludeRut === true ? params.invoiceRutDigits?.replace(/\D/g, "").slice(0, 12) ?? null : null,
  };
}

export function blobToFile(blob: SerializedFileBlob): File {
  return new File([blob.buffer], blob.name, { type: blob.type });
}

export async function buildIncomeCertificatePaymentResume(
  checkoutId: string,
  payload: IncomeCertificateSubmitPayload,
  invoice?: { invoiceIncludeRut: boolean; invoiceRutDigits?: string | null },
): Promise<CertificatePaymentResumeRecordV1> {
  const uploads: Array<{ docType: UserNotarialRequestDocumentType } & SerializedFileBlob> =
    await Promise.all(
      payload.uploads.map(async (u) => {
        const b = await fileToBlob(u.file);
        return { docType: u.type, ...b };
      }),
    );
  return {
    checkoutId,
    version: 1,
    kind: "income_certificate",
    fulfillmentMode: payload.fulfillmentMode,
    incomeCertificateDetails: payload.incomeCertificateDetails,
    companyRutUnverified: payload.companyRutUnverified === true,
    companySnapshot: payload.companySnapshot,
    uploads,
    invoiceIncludeRut: invoice?.invoiceIncludeRut,
    invoiceRutDigits:
      invoice?.invoiceIncludeRut === true
        ? invoice.invoiceRutDigits?.replace(/\D/g, "").slice(0, 12) ?? null
        : null,
  };
}
